import { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetOrganizationById } from '../../organizations/hooks/get-organization-by-id.hook';
import { useAdminGetProducts } from '../../products/hooks/admin-get-products.hook';
import { useAdminGetBaskets } from '../../baskets/hooks/admin-get-baskets.hook';
import { useAdminCreateOrder } from '../hooks/admin-create-order.hook';
import { useOrderFormStore } from '../store/order-form.store';
import { CatalogContainer } from '../components/Containers/CatalogContainer.component';
import { OrderSummaryContainer } from '../components/Containers/OrderSummaryContainer.component';
import { DeliveryCostSection } from '../components/Containers/DeliveryCostSection.component';
import { OrderActionsSection } from '../components/Containers/OrderActionsSection.component';
import { OrganizationDeliveryInfoSection } from '../components/Containers/OrganizationDeliveryInfoSection.component';
import { buildOwnerOptions } from '../components/Forms/OrganizationOwnerSelect.component';
import {
  buildOrganizationOrderPayload,
  OrganizationOrderOwner,
} from '../utils/build-organization-order-payload.util';
import { OrganizationDeliveryPoint } from '../../organizations/types/organization.type';
import { DeliveryDay } from '../types/delivery-selection.type';
import { ORDER_STATUSES, OrderStatus } from '../types/order-status.type';
import { ORDER_ADV_STATUSES, OrderAdvStatus } from '../types/order-adv-status.type';
import { ADMIN_ORDER_APP_PATHS } from '../constants/orders-app-paths.constant';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',

    '& h4': {
      margin: 0,
      opacity: 0.7,
    },
  },
  header: {
    '& > h2': {
      margin: 0,
    },
  },
  offerBanner: {
    margin: 0,
    padding: '0.5rem 0.75rem',
    borderRadius: 4,
    backgroundColor: 'rgba(46, 125, 50, 0.08)',
  },
  bottom: {
    display: 'grid',
    gridTemplateColumns: '3fr 0.5fr 1fr',
    gap: '1rem',
  },
});

export default function AdminCreateOrganizationOrderView() {
  const classes = useStyles();
  const { organizationId } = useParams<{ organizationId: string }>();
  const location = useLocation();
  const history = useHistory();
  const preselectedUserId = new URLSearchParams(location.search).get('userId') ?? undefined;

  const { isLoading: isLoadingOrganization, organization } = useAdminGetOrganizationById({ organizationId });
  const { isLoading: isLoadingProducts } = useAdminGetProducts({ filters: { isActive: true } });
  const { isLoading: isLoadingBaskets } = useAdminGetBaskets({ filters: { isActive: true } });
  const { isCreating, createOrder } = useAdminCreateOrder();

  const [items, addProduct, addBasket, addVirtual, increaseQuantity, decreaseQuantity, changeQuantity, initialize, reset] =
    useOrderFormStore((state) => [
      state.items,
      state.addProduct,
      state.addBasket,
      state.addVirtual,
      state.increaseQuantity,
      state.decreaseQuantity,
      state.changeQuantity,
      state.initialize,
      state.reset,
    ]);

  const [selectedOwner, setSelectedOwner] = useState<OrganizationOrderOwner | null>(null);
  const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState<OrganizationDeliveryPoint | null>(null);
  const [selectedDay, setSelectedDay] = useState<DeliveryDay | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus>(ORDER_STATUSES.PAID);
  const [selectedAdvStatus, setSelectedAdvStatus] = useState<OrderAdvStatus>(ORDER_ADV_STATUSES.INPUT);
  const [selectedDeliveryCost, setSelectedDeliveryCost] = useState(0);
  const [hasInitializedOwner, setHasInitializedOwner] = useState(false);

  useEffect(() => {
    initialize([]);
    return () => reset();
  }, []);

  const isLoading = isLoadingOrganization || isLoadingProducts || isLoadingBaskets;
  if (isLoading || !organization) return <Skeleton variant="text" width="100%" height={50} />;

  const deliveryPoints = organization.deliveryPoints ?? [];

  if (!hasInitializedOwner) {
    const ownerOptions = buildOwnerOptions(organization);
    const preselectedOwner = preselectedUserId
      ? ownerOptions.find((ownerOption) => ownerOption.id === preselectedUserId)
      : undefined;
    setSelectedOwner(preselectedOwner ?? ownerOptions[0] ?? null);
    setHasInitializedOwner(true);
  }

  if (deliveryPoints.length === 0) {
    return (
      <section className={classes.root}>
        <section className={classes.header}>
          <h2>Création de commande — {organization.name}</h2>
        </section>
        <p>
          Cette organisation n&apos;a aucun point de livraison. Ajoutez-en depuis l&apos;onglet « Points de
          livraison » de sa fiche avant de créer une commande.
        </p>
      </section>
    );
  }

  const canSubmit = items.length > 0 && !!selectedOwner && !!selectedDeliveryPoint && !!selectedDay;

  const handleSubmit = async () => {
    if (!selectedOwner || !selectedDeliveryPoint || !selectedDay) return;

    const payload = buildOrganizationOrderPayload({
      organization,
      owner: selectedOwner,
      deliveryPointId: selectedDeliveryPoint.id,
      items,
      deliveryDay: selectedDay,
      status: selectedStatus,
      advStatus: selectedAdvStatus,
      deliveryCost: selectedDeliveryCost,
    });

    const created = await createOrder(payload);
    if (created) history.push(ADMIN_ORDER_APP_PATHS.ORDER_DETAILS(created.id));
  };

  return (
    <section className={classes.root}>
      <section className={classes.header}>
        <h2>Création de commande — {organization.name}</h2>
        {organization.offer.discount > 0 && (
          <p className={classes.offerBanner}>
            Offre <strong>{organization.offer.code}</strong> : −{organization.offer.discount}
            {organization.offer.reduction === 'percent' ? ' %' : ' €'} appliquée à la commande
          </p>
        )}
      </section>
      <CatalogContainer onAddProduct={addProduct} onAddBasket={addBasket} onAddVirtual={addVirtual} />
      <OrderSummaryContainer
        items={items}
        onIncrease={increaseQuantity}
        onDecrease={decreaseQuantity}
        onChangeQuantity={changeQuantity}
        companyDiscountPercent={organization.offer.reduction === 'percent' ? organization.offer.discount : undefined}
        globalDiscount={
          organization.offer.reduction === 'flat' && organization.offer.discount > 0
            ? { value: organization.offer.discount, reduction: 'flat' }
            : undefined
        }
        deliveryCost={selectedDeliveryCost}
      />
      <section className={classes.bottom}>
        <OrganizationDeliveryInfoSection
          organization={organization}
          selectedOwner={selectedOwner}
          selectedDeliveryPoint={selectedDeliveryPoint}
          selectedDay={selectedDay}
          onOwnerChange={setSelectedOwner}
          onDeliveryPointChange={setSelectedDeliveryPoint}
          onDayChange={setSelectedDay}
        />
        <DeliveryCostSection value={selectedDeliveryCost} onChange={setSelectedDeliveryCost} />
        <OrderActionsSection
          status={selectedStatus}
          advStatus={selectedAdvStatus}
          onStatusChange={setSelectedStatus}
          onAdvStatusChange={setSelectedAdvStatus}
          onSubmit={handleSubmit}
          canSubmit={canSubmit}
          submitLabel="Créer la commande"
          isSubmitting={isCreating}
        />
      </section>
    </section>
  );
}
